import useDeadlineCountdown from "@root/bet_utils/use_deadline_countdown";
import { BaseBet, PickType, WithSecondsLeft } from "./types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import savePicks from "@root/bet_utils/save_picks";
import { createErrorResult, createOkResult } from "@root/bet_utils/DisplaySaveResult";
import _ from "lodash";
import Axios from "axios";

const POLL_FOR_SCORING_UPDATE_INTERVAL_MIL = 2 * 60 * 1000 // every 2 min

function useEnrichBet<T extends BaseBet>(initialBet: T, authToken: string, updateBetFromScoringUpdateResponse?: (prev: T, fromScoringUpdate: T) => T) {
  const secondsLeft = useDeadlineCountdown(initialBet.deadline);
  const [ _bet, _setBet ] = useState<T>(initialBet);
  const _betRef = useRef(_bet)
  const updateBetFromScoringUpdateResponseRef = useRef(updateBetFromScoringUpdateResponse)
  updateBetFromScoringUpdateResponseRef.current = updateBetFromScoringUpdateResponse
  const authTokenRef = useRef(authToken)
  authTokenRef.current = authToken
  _betRef.current = _bet
  const [ displaySaveResult, setDisplaySaveResult ] = useState<ReturnType<typeof createOkResult>>()

  const changePicks = useCallback((newPicks: PickType<T>[]) => {
    _setBet((prev) => ({ ...prev, picks: newPicks })) // force component update
    savePicks({ bet_id: initialBet.bet_id }, authTokenRef.current, newPicks,
      /* OK    */ (msg: string) => { setDisplaySaveResult(createOkResult(msg)) },
      /* ERROR */ (errorMsg: string) => { setDisplaySaveResult(createErrorResult(errorMsg)) })
  }, [initialBet.bet_id]);

  useEffect(() => { // close bet on deadline
    if (secondsLeft <= 0 && _bet.event_state === 'open') {
      _setBet((prev) => ({ ...prev, event_state: 'progress' }))
    }
  }, [secondsLeft <= 0, _bet.event_state])

  const shallPollForScoringUpdate =
    !_bet.from_other // only poll updates for own bets!
    && (_bet.event_state === "progress" || _bet.event_state === "over")    
    && !(_bet.scoring_state === "final")

  useEffect(() => {
    let interval: number
    if (shallPollForScoringUpdate) {
      interval = window.setInterval(() => {
        const data = {
          bet_id: _betRef.current.bet_id,
          authenticity_token: authTokenRef.current,
        }

        Axios.post('/bet/scoring_update', data).then((response) => {
          const data = response.data as ({ error?: string, bet?: T })
          if (data.error) {
            console.warn(`scoring_update error for bet ${_betRef.current.bet_id}`, data.error )
          }
          const receivedBet = data.bet
          if (receivedBet) {
            _setBet((prev) => {
              const updated: T = {
                ...prev,
                event_state: receivedBet.event_state,
                scoring_state: receivedBet.scoring_state,
                total_score: receivedBet.total_score,
                temp_scored: receivedBet.temp_scored
              }
              if (updateBetFromScoringUpdateResponseRef.current) {
                return updateBetFromScoringUpdateResponseRef.current(updated, receivedBet)
              }
              return updated
            })
          }
        }).catch((err) => {
          console.warn("could not fetch scoring_update", err)
        })

      }, POLL_FOR_SCORING_UPDATE_INTERVAL_MIL)
    }

    return () => { // cleanup
      if (_.isNumber(interval)) {
        window.clearInterval(interval)
      }
    }
  }, [shallPollForScoringUpdate])

  return useMemo(() => ({
    bet: { ..._bet, secondsLeft },
    displaySaveResult,
    changePicks,
  }), [_bet, secondsLeft, displaySaveResult, changePicks]);
}

export type EnrichedBet<T extends BaseBet> = ReturnType<typeof useEnrichBet<T>>

export default useEnrichBet
